import React, { ReactElement, useMemo } from 'react';
import styles from './InvestigationMap.module.scss';
import MapView, { MapViewProps } from '@customer/components/maps/map-view/MapView';
import AllGeofencesLayer from '@customer/components/maps/all-geofences-layer/AllGeofencesLayer';
import {
  TrackModel,
  useGetInvestigationQuery,
  useGetTracksQuery,
} from '@customer/store/apis/investigationsApi';
import { useParams } from 'react-router-dom';
import { skipToken } from '@reduxjs/toolkit/query';
import { useAppSelector } from '@customer/store/hooks';
import { latlngAustralia } from '@customer/components/maps/hooks/useLastMapPositionData';
import { LatLng, latLngBounds } from 'leaflet';
import MapLoader from '@customer/components/maps/map-loader/MapLoader';
import { BeamsLayerGroup } from '@customer/components/maps/beams-layer-group/BeamsLayerGroup';
import { sortInvestigationAssets } from '../assets/InvestigationAssets';
import { GeofenceTooltipContextProvider } from '@customer/components/maps/geofence-layer-group/GeofenceTooltipContextProvider';
import GeofenceLayerGroup from '@customer/components/maps/geofence-layer-group/GeofenceLayerGroup';
import GeofenceTooltipController from '@customer/components/maps/geofence-tooltip/GeofenceTooltipController';
import { Pane } from 'react-leaflet';
import { CustomPaneName, CustomPaneZIndex } from '@customer/components/maps/mapConfig';
import TracksMarkersGroup from '@customer/components/maps/tracks-markers-group/TracksMarkersGroup';
import TracksGeojsonGroup from '@customer/components/maps/tracks-geojson-group/TracksGeojsonGroup';
import { RealSizeControl } from '../real-size-control/RealSizeControl';

const fallbackMapProps = {
  initialCentre: latlngAustralia,
  initialZoom: 4,
};

export const InvestigationMap = (): ReactElement => {
  const { investigationId } = useParams();

  const siteId = useAppSelector(state => state.auth.siteId);

  const isSkipQuery = typeof investigationId === 'undefined' || typeof siteId === 'undefined';

  const { data: investigation, isSuccess: isInvestigationLoaded } = useGetInvestigationQuery(
    isSkipQuery ? skipToken : { siteId, investigationId },
    { refetchOnMountOrArgChange: 60 },
  );

  // Defer loading until the investigation is fully loaded, since the backend could be updating track data.

  const { currentData, isFetching } = useGetTracksQuery(investigationId ?? skipToken, {
    refetchOnMountOrArgChange: 60,
    skip:
      typeof investigationId === 'undefined' ||
      typeof siteId === 'undefined' ||
      !isInvestigationLoaded,
  });

  const { sLat, sLon, eLat, eLon, tracks, queryKey = 'no-query-made' } = currentData ?? {};

  const mapProps = useMemo<MapViewProps>(() => {
    if (
      typeof sLat === 'number' &&
      typeof sLon === 'number' &&
      typeof eLat === 'number' &&
      typeof eLon === 'number'
    ) {
      const nw = new LatLng(sLat, sLon);
      const se = new LatLng(eLat, eLon);
      return { bounds: latLngBounds([nw, se]) };
    }

    return fallbackMapProps;
  }, [sLat, sLon, eLat, eLon]);

  const showMap = typeof currentData !== 'undefined';

  // Sort the tracks so that Local and Remote are at the top of the pile.
  const sortedTracks = useMemo<TrackModel[]>(
    () => (typeof tracks !== 'undefined' ? [...tracks].sort(sortInvestigationAssets) : []),
    [tracks],
  );

  return (
    <div className={styles.card}>
      <div className={styles.overlayContainer}>
        <MapLoader showMap={showMap} isFetching={isFetching}>
          {/* Pass the unique queryKey to the internal MapManager to correctly set the map view after a response. */}
          <MapView {...mapProps} renderKey={queryKey} showLayersControl={true} padding={true}>
            {investigation && investigation.loGeoCfg !== null && (
              <GeofenceTooltipContextProvider>
                <GeofenceLayerGroup
                  layerNameTrans="LOGeofences"
                  checked={false}
                  geoJson={investigation.loGeoCfg}
                >
                  <GeofenceTooltipController />
                </GeofenceLayerGroup>
              </GeofenceTooltipContextProvider>
            )}
            {investigation && investigation.siteGeoCfg !== null && (
              <GeofenceTooltipContextProvider>
                <GeofenceLayerGroup
                  layerNameTrans="SiteGeofences"
                  checked={false}
                  geoJson={investigation.siteGeoCfg}
                >
                  <GeofenceTooltipController />
                </GeofenceLayerGroup>
              </GeofenceTooltipContextProvider>
            )}
            <AllGeofencesLayer checked={false} />
            {siteId && investigationId && (
              // Use the queryKey to re-render the tracks when a new batch of data is received.
              <React.Fragment key={queryKey}>
                <Pane
                  name={CustomPaneName.TrackRoutes}
                  style={{ zIndex: CustomPaneZIndex.TrackRoutes }}
                >
                  <TracksGeojsonGroup
                    siteId={siteId}
                    investigationId={investigationId}
                    tracks={sortedTracks}
                    checked={true}
                  />
                </Pane>
                <Pane
                  name={CustomPaneName.TrackPoints}
                  style={{ zIndex: CustomPaneZIndex.TrackPoints }}
                />
                <Pane name={CustomPaneName.Beams} style={{ zIndex: CustomPaneZIndex.Beams }}>
                  <BeamsLayerGroup
                    siteId={siteId}
                    investigationId={investigationId}
                    tracks={sortedTracks}
                    checked={true}
                  />
                </Pane>
                {/* Create the 'Objects' pane, but don't wrap the marker tooltips in it */}
                <Pane name={CustomPaneName.Objects} style={{ zIndex: CustomPaneZIndex.Objects }} />
                <TracksMarkersGroup
                  siteId={siteId}
                  investigationId={investigationId}
                  tracks={sortedTracks}
                  checked={true}
                  pane={CustomPaneName.Objects} // Provide the pane for the markers
                  investigationType={investigation?.it}
                />
              </React.Fragment>
            )}
          </MapView>
        </MapLoader>
        <div className={styles.overlayControls}>
          <RealSizeControl />
        </div>
      </div>
    </div>
  );
};

export default InvestigationMap;
